/**
 * Settings → Document Templates.
 *
 * Spec: .claude/specs/document-templates.md (Package B AC10/AC12/AC17, Package E).
 *
 * Two things live here: the wording (templates, per document type) and the
 * appearance (one theme for all documents). They are separate sections because
 * clients complain about both and mean different things by "we want our own
 * template".
 *
 * Every document type is listed, including those with no template — those keep
 * using the built-in AI prompt, and saying so is the point: adoption is per
 * type, at the client's pace.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { useConfirm } from "../../context/ConfirmContext";
import { AsyncButton } from "../../components/AsyncButton";
import {
  deleteDocTemplate, duplicateDocTemplate, getDocTemplatePermissions, getDocTemplates,
  getDocTemplateTypes, getDocumentTheme, setDocTemplateDelegation,
  resetDocumentTheme, saveDocumentTheme, setDocTemplateActive, setDocTemplateDefault,
} from "../../api";

const NAVY = "#1e3a8a";

const btnPrimary: React.CSSProperties = {
  background: NAVY, color: "white", border: "1px solid transparent",
  padding: "8px 15px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13,
};
const btnSecondary: React.CSSProperties = {
  background: "white", color: "#334155", border: "1px solid #cbd5e1",
  padding: "6px 12px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 12,
};
const btnGhost: React.CSSProperties = {
  background: "transparent", color: "#64748b", border: "none",
  padding: "6px 10px", cursor: "pointer", fontWeight: 600, fontSize: 12,
};
const inputStyle: React.CSSProperties = {
  width: "100%", padding: "8px 11px", borderRadius: 6, border: "1px solid #e2e8f0",
  fontSize: 13, color: "#0f172a", boxSizing: "border-box",
};
const labelStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: "#334155", display: "block", marginBottom: 5,
};

function Pill({ tone, children }: { tone: "ok" | "mute" | "warn"; children?: React.ReactNode }) {
  const tones = {
    ok:   { bg: "#f0fdf4", fg: "#16a34a", ln: "#86efac" },
    warn: { bg: "#fffbeb", fg: "#b45309", ln: "#fde68a" },
    mute: { bg: "#f1f5f9", fg: "#64748b", ln: "#e2e8f0" },
  }[tone];
  return (
    <span style={{
      background: tones.bg, color: tones.fg, border: `1px solid ${tones.ln}`,
      borderRadius: 999, padding: "2px 9px", fontSize: 11, fontWeight: 600,
      whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

export default function DocumentTemplatesTab() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const confirm = useConfirm();
  const canPublish = ["admin", "admin_assistant"].includes(user?.role || "");

  const [types, setTypes] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState<any>(null);
  const [themeMeta, setThemeMeta] = useState<any>(null);
  const [showTheme, setShowTheme] = useState(false);
  const [perms, setPerms] = useState<any>(null);
  const [showPerms, setShowPerms] = useState(false);

  async function load() {
    try {
      const [t, tpl, th, pm] = await Promise.all([
        getDocTemplateTypes(), getDocTemplates(), getDocumentTheme(),
        getDocTemplatePermissions(),
      ]);
      setTypes(t || []);
      setTemplates(tpl || []);
      setTheme(th.theme);
      setThemeMeta(th);
      setPerms(pm);
    } catch {
      toast("Could not load document templates", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  async function makeDefault(id: number) {
    try {
      await setDocTemplateDefault(id);
      toast("This template is now used for that document type", "success");
      load();
    } catch (e: any) {
      toast(e?.response?.data?.detail || "Could not set the default", "error");
    }
  }

  async function toggleActive(tpl: any) {
    try {
      await setDocTemplateActive(tpl.id, !tpl.is_active);
      toast(tpl.is_active ? "Template deactivated" : "Template reactivated", "success");
      load();
    } catch {
      toast("Could not change the template", "error");
    }
  }

  async function duplicate(tpl: any) {
    try {
      const copy = await duplicateDocTemplate(tpl.id);
      toast(`Created "${copy.name}" — not live until an admin makes it the default`, "success");
      navigate(`/settings/document-templates/${copy.id}`);
    } catch {
      toast("Could not duplicate the template", "error");
    }
  }

  async function remove(tpl: any) {
    const ok = await confirm(
      `Delete "${tpl.name}"? If documents were generated from it, it will be deactivated ` +
      `instead so their wording history stays intact.`,
      { title: "Delete template", confirmLabel: "Delete", danger: true },
    );
    if (!ok) return;
    try {
      const out = await deleteDocTemplate(tpl.id);
      toast(out.deactivated ? out.message : "Template deleted", out.deactivated ? "info" : "success");
      load();
    } catch {
      toast("Could not delete the template", "error");
    }
  }

  async function persistTheme() {
    try {
      const out = await saveDocumentTheme(theme);
      setTheme(out.theme);
      setThemeMeta(out);
      toast("Document appearance saved", "success");
    } catch {
      toast("Could not save the appearance", "error");
    }
  }

  async function clearTheme() {
    try {
      const out = await resetDocumentTheme();
      setTheme(out.theme);
      setThemeMeta(out);
      toast("Back to the standard appearance", "success");
    } catch {
      toast("Could not reset", "error");
    }
  }

  async function delegate(docType: string, roles: string[]) {
    try {
      setPerms(await setDocTemplateDelegation(docType, roles));
      toast(roles.length
        ? `${docType.replace(/_/g, " ")} templates can now be authored by ${roles.join(", ")}`
        : `${docType.replace(/_/g, " ")} templates are back to the default roles`,
        "success");
      load();
    } catch (e: any) {
      toast(e?.response?.data?.detail || "Could not change who may author", "error");
    }
  }

  if (loading) {
    return <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>Loading…</div>;
  }

  const byType = (family: string, docType: string) =>
    templates.filter(t => t.doc_family === family && t.doc_type === docType);

  const families: [string, string][] = [
    ["hr_letter", "HR Letters"],
    ["offer", "Offer Letters"],
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 26 }}>
      <div>
        <h3 style={{ margin: "0 0 4px", fontSize: 16, color: "#0f172a" }}>Document wording</h3>
        <p style={{ margin: 0, fontSize: 13, color: "#64748b", lineHeight: 1.6 }}>
          Save your own letter wording, with your clauses fixed exactly and AI writing only
          the paragraphs that change per person. A type with no template keeps using the
          built-in AI prompt.
        </p>
      </div>

      {families.map(([family, familyLabel]) => {
        const rows = types.filter(t => t.doc_family === family);
        if (!rows.length) return null;
        return (
          <div key={family} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, letterSpacing: ".07em",
              textTransform: "uppercase", color: "#64748b",
            }}>{familyLabel}</div>

            {rows.map(row => {
              const mine = byType(family, row.doc_type);
              const def = mine.find(t => t.is_default && t.is_active);
              return (
                <div key={`${family}:${row.doc_type}`} style={{
                  background: mine.length ? "white" : "#f8fafc",
                  border: `1px ${mine.length ? "solid" : "dashed"} #e2e8f0`,
                  borderRadius: 10, padding: "13px 15px",
                  display: "flex", justifyContent: "space-between",
                  alignItems: "center", gap: 12, flexWrap: "wrap",
                }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      fontWeight: 600, fontSize: 14,
                      color: mine.length ? "#0f172a" : "#64748b",
                      display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                    }}>
                      {row.label}
                      {def && <Pill tone="ok">Default</Pill>}
                      {def?.has_pending_changes && (
                        <Pill tone="warn">
                          {canPublish ? "Changes awaiting your approval" : "Changes awaiting approval"}
                        </Pill>
                      )}
                      {def && !def.uses_ai && <Pill tone="mute">No AI credits</Pill>}
                    </div>
                    <div style={{
                      fontSize: 12, color: "#64748b", marginTop: 3,
                      fontVariantNumeric: "tabular-nums",
                    }}>
                      {def ? (
                        <>
                          {def.name} · v{def.live_version ?? def.version} live
                          {def.has_pending_changes && <> (v{def.version} pending)</>}
                          {" "}· {def.block_count} blocks{" "}
                          ({def.block_count - def.ai_block_count} fixed, {def.ai_block_count} AI)
                          {typeof def.document_count === "number" && (
                            <> · {def.document_count} document{def.document_count === 1 ? "" : "s"} issued</>
                          )}
                          {def.updated_by && <> · last edited by {def.updated_by}</>}
                        </>
                      ) : mine.length ? (
                        <>{mine.length} template{mine.length === 1 ? "" : "s"}, none set as default —
                          still using the built-in AI prompt</>
                      ) : (
                        <>No template — AI writes the whole document from the built-in prompt</>
                      )}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 7, alignItems: "center", flexWrap: "wrap" }}>
                    {def && (
                      <button onClick={() => navigate(`/settings/document-templates/${def.id}`)}
                        style={btnSecondary}>Edit</button>
                    )}
                    {def && (
                      <button onClick={() => duplicate(def)} style={btnGhost}>Duplicate</button>
                    )}
                    <button
                      onClick={() => navigate(
                        `/settings/document-templates/new?family=${family}&type=${row.doc_type}&import=1`)}
                      style={mine.length ? btnGhost : btnSecondary}>
                      {mine.length ? "+ Add another" : "Create template"}
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Non-default templates for these types, so they are reachable. */}
            {rows.flatMap(row => byType(family, row.doc_type).filter(t => !(t.is_default && t.is_active)))
              .map(tpl => (
                <div key={tpl.id} style={{
                  marginLeft: 18, background: "#f8fafc", border: "1px solid #e2e8f0",
                  borderRadius: 8, padding: "10px 13px", display: "flex",
                  justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap",
                }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#334155",
                                  display: "flex", gap: 8, alignItems: "center" }}>
                      {tpl.name}
                      {!tpl.is_active && <Pill tone="warn">Inactive</Pill>}
                    </div>
                    <div style={{ fontSize: 12, color: "#64748b" }}>
                      {tpl.doc_type_label} · v{tpl.version} · {tpl.block_count} blocks
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 7, alignItems: "center" }}>
                    <button onClick={() => navigate(`/settings/document-templates/${tpl.id}`)}
                      style={btnSecondary}>Edit</button>
                    {canPublish && tpl.is_active && (
                      <button onClick={() => makeDefault(tpl.id)} style={btnSecondary}>
                        Use for this type
                      </button>
                    )}
                    {canPublish && (
                      <button onClick={() => toggleActive(tpl)} style={btnGhost}>
                        {tpl.is_active ? "Deactivate" : "Reactivate"}
                      </button>
                    )}
                    {canPublish && (
                      <button onClick={() => remove(tpl)}
                        style={{ ...btnGhost, color: "#dc2626" }}>Delete</button>
                    )}
                  </div>
                </div>
              ))}
          </div>
        );
      })}

      {perms && (
        <div style={{
          background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10,
          padding: "14px 16px", fontSize: 12.5, color: "#334155", lineHeight: 1.65,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between",
                        alignItems: "flex-start", gap: 12 }}>
            <div>
              <strong style={{ color: "#0f172a" }}>Who can do what here</strong>
              <div style={{ marginTop: 5, color: "#64748b" }}>
                You are signed in as <strong>{perms.your_role}</strong>. You{" "}
                {perms.you_can_author_any ? "can" : "cannot"} write templates, and you{" "}
                {perms.you_can_publish ? "can" : "cannot"} decide which version goes live.
                {!perms.you_can_publish &&
                  " An admin approves changes before they reach a document."}
              </div>
            </div>
            {canPublish && (
              <button onClick={() => setShowPerms(s => !s)} style={btnSecondary}>
                {showPerms ? "Hide" : "Change who can author"}
              </button>
            )}
          </div>

          {showPerms && canPublish && (
            <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ fontSize: 12, color: "#64748b" }}>
                Unset means the default ({perms.default_author_roles.join(", ")}).
                Clearing every role returns a type to that default.
              </div>
              {perms.types.map((row: any) => (
                <div key={`${row.doc_family}:${row.doc_type}`} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  gap: 10, flexWrap: "wrap", background: "white",
                  border: "1px solid #e2e8f0", borderRadius: 8, padding: "9px 12px",
                }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{row.label}</div>
                    <div style={{ fontSize: 11.5, color: "#64748b" }}>
                      {row.author_roles.join(", ")}
                      {!row.delegated && " (default)"}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {perms.delegatable_roles.map((role: string) => {
                      const on = row.author_roles.includes(role);
                      return (
                        <button key={role}
                          onClick={() => delegate(row.doc_type, on
                            ? row.author_roles.filter((r: string) => r !== role)
                            : [...row.author_roles, role])}
                          style={{
                            fontSize: 11, fontWeight: 600, padding: "3px 9px",
                            borderRadius: 999, cursor: "pointer",
                            background: on ? "#eff6ff" : "white",
                            color: on ? NAVY : "#64748b",
                            border: `1px solid ${on ? "#bfdbfe" : "#e2e8f0"}`,
                          }}>
                          {role}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Appearance ── */}
      <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: 22 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            <h3 style={{ margin: "0 0 4px", fontSize: 16, color: "#0f172a" }}>Document appearance</h3>
            <p style={{ margin: 0, fontSize: 13, color: "#64748b", lineHeight: 1.6 }}>
              Colours, typeface and letterhead layout for every document — letters, offers and
              certificates.{" "}
              {themeMeta?.is_default
                ? "Currently using the standard appearance."
                : "Customised."}
            </p>
          </div>
          <button onClick={() => setShowTheme(s => !s)} style={btnSecondary}>
            {showTheme ? "Hide" : "Customise"}
          </button>
        </div>

        {showTheme && theme && (
          <div style={{
            marginTop: 14, background: "#f8fafc", border: "1px solid #e2e8f0",
            borderRadius: 10, padding: 16, display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14,
          }}>
            <div>
              <label style={labelStyle}>Heading colour</label>
              <input type="color" value={theme.primary}
                onChange={e => setTheme({ ...theme, primary: e.target.value })}
                style={{ ...inputStyle, padding: 3, height: 36 }} />
            </div>
            <div>
              <label style={labelStyle}>Rule / accent colour</label>
              <input type="color" value={theme.accent}
                onChange={e => setTheme({ ...theme, accent: e.target.value })}
                style={{ ...inputStyle, padding: 3, height: 36 }} />
            </div>
            <div>
              <label style={labelStyle}>Typeface</label>
              <select value={theme.body_font}
                onChange={e => setTheme({ ...theme, body_font: e.target.value })}
                style={inputStyle}>
                {(themeMeta?.font_options || []).map((f: any) => (
                  <option key={f.key} value={f.key}>{f.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Letterhead layout</label>
              <select value={theme.header_variant}
                onChange={e => setTheme({ ...theme, header_variant: e.target.value })}
                style={inputStyle}>
                {(themeMeta?.header_variants || []).map((v: any) => (
                  <option key={v.key} value={v.key}>{v.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>
                Page margin (mm) — {themeMeta?.margin_range?.[0]}–{themeMeta?.margin_range?.[1]}
              </label>
              <input type="number" value={theme.margin_mm}
                min={themeMeta?.margin_range?.[0]} max={themeMeta?.margin_range?.[1]}
                onChange={e => setTheme({ ...theme, margin_mm: Number(e.target.value) })}
                style={inputStyle} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>Footer text</label>
              <input value={theme.footer_text}
                onChange={e => setTheme({ ...theme, footer_text: e.target.value })}
                placeholder="Leave blank for the standard computer-generated notice"
                style={inputStyle} />
            </div>
            <div style={{ gridColumn: "1 / -1", display: "flex", gap: 18, flexWrap: "wrap" }}>
              {[
                ["show_ref", "Show reference number"],
                ["show_to_block", "Show the To: address block"],
                ["show_subject", "Show the subject line"],
              ].map(([key, label]) => (
                <label key={key} style={{
                  display: "flex", gap: 7, alignItems: "center",
                  fontSize: 12.5, color: "#334155", cursor: "pointer",
                }}>
                  <input type="checkbox" checked={!!theme[key]}
                    onChange={e => setTheme({ ...theme, [key]: e.target.checked })} />
                  {label}
                </label>
              ))}
            </div>
            <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={clearTheme} style={btnGhost}>Reset to standard</button>
              <AsyncButton onClick={persistTheme} style={btnPrimary}>Save appearance</AsyncButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
